/**
 * Gemeinsame Typen, Beschriftungen und Formatierer des KPI-Cockpits.
 *
 * Bewusst KEINE Komponenten in dieser Datei: gemischte Exporte (Komponente +
 * Konstante) loesen `react-refresh/only-export-components` aus.
 */
import type { FlatRateCoverage, ReportMetadata } from '../../types';
import { DOCUMENT_TYPE_LABEL } from '../../types';
import type { CockpitPeriod } from '../../services/cockpitTypes';
import { formatCentsPlain } from '../../services/money';

// Kein 'api' mehr: die API-Logs sind Betriebsdaten und stehen seit dem
// Menue-Umbau (19.10.2026) unter System → „API-Logs".
export type CockpitTab =
    | 'uebersicht' | 'auslastung' | 'team' | 'projekte' | 'gantt' | 'angebote';

const COCKPIT_TABS: readonly string[] = ['uebersicht', 'auslastung', 'team', 'projekte', 'gantt', 'angebote'] satisfies CockpitTab[];

/**
 * Gemerkter Reiter → gueltiger Reiter. Der Wert unter `admin.cockpit.tab`
 * kann von vor dem Umbau stammen ('api'); ungeprueft zeigte das Cockpit dann
 * eine leere Flaeche ohne markierten Reiter.
 */
export const gueltigerCockpitTab = (wert: string | null | undefined): CockpitTab =>
    wert && COCKPIT_TABS.includes(wert) ? (wert as CockpitTab) : 'uebersicht';
export type PeriodKind = 'year' | 'quarter' | 'month';

export const MO = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
export const MO_FULL = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

/** Kapazitaet je Person und Monat — identisch zur bisherigen Ansicht. */
export const CAP_HOURS = 160;
export const CAP_MINUTES = CAP_HOURS * 60;

/**
 * Die Felder einer `project_services`-Zeile, die das Cockpit liest.
 * Lokal typisiert statt `any`, damit die Ratsche nichts zu zaehlen hat.
 */
export interface CockpitService {
    id: string;
    title?: string | null;
    unit?: string | null;
    assignment?: string | null;
    lv_position?: string | null;
    unit_price?: number | string | null;
    is_optional?: boolean | null;
    quantity_contracted?: number | string | null;
    quantity_used?: number | string | null;
    quantity_billed?: number | string | null;
    /** VERZICHT — erbracht, aber ausdruecklich nicht mehr gefordert (projectLv.waivedQuantity). */
    quantity_waived?: number | string | null;
    flat_rate?: FlatRateCoverage | null;
    /**
     * PAKETPOSITION (SQL Nr. 165) — roh aus der Spalte, gelesen nur ueber
     * projectLv.bestandteilFelderAusZeile (dort steht die eine Lesart).
     */
    bestandteile?: unknown;
    /** Gate der Paketposition, von der Datenbank gepflegt (Nr. 166). */
    bestandteile_offen?: number | string | null;
}

/** Die Felder einer Projektzeile, die das Cockpit liest. */
export interface CockpitProject {
    id: string;
    name: string;
    project_number?: string | null;
    status?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    updated_at?: string | null;
    user_id?: string | null;
    customer_name?: string | null;
    coordinator_name?: string | null;
    ist_uebung?: boolean | null;
    project_services?: CockpitService[] | null;
    data?: { metadata?: ReportMetadata } | null;
}

export interface StatusChip { label: string; text: string; bg: string; border: string; }

const STATUS: Record<string, StatusChip> = {
    active: { label: 'Aktiv', text: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
    planning: { label: 'Planung', text: 'text-sky-600', bg: 'bg-sky-50', border: 'border-sky-200' },
    on_hold: { label: 'Ruht', text: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
    completed: { label: 'Beendet', text: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200' },
    finished: { label: 'Archiv', text: 'text-slate-500', bg: 'bg-slate-50', border: 'border-slate-200' },
};

export const statusChip = (status: string | null | undefined): StatusChip =>
    STATUS[String(status ?? 'planning')] ?? STATUS.planning;

/**
 * Heutiges Datum als lokales ISO-Datum, aus Ziffern gebaut.
 * `toISOString()` waere UTC und in Deutschland abends der Folgetag.
 */
export const localIsoToday = (): string => {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export const makePeriod = (year: number, kind: PeriodKind, value: number): CockpitPeriod => {
    if (kind === 'quarter') {
        const q = Math.min(4, Math.max(1, value)) as 1 | 2 | 3 | 4;
        return { year, kind: 'quarter', quarter: q };
    }
    if (kind === 'month') return { year, kind: 'month', month: Math.min(12, Math.max(1, value)) };
    return { year, kind: 'year' };
};

/** Juli 2026 / Q3 2026 / 2026 */
export const periodLabel = (p: CockpitPeriod): string => {
    if (p.kind === 'month') return `${MO_FULL[p.month - 1]} ${p.year}`;
    if (p.kind === 'quarter') return `Q${p.quarter} ${p.year}`;
    return String(p.year);
};

/** Kurzform fuer Dateinamen. */
export const periodSlug = (p: CockpitPeriod): string => {
    if (p.kind === 'month') return `${p.year}-${String(p.month).padStart(2, '0')}`;
    if (p.kind === 'quarter') return `${p.year}-Q${p.quarter}`;
    return String(p.year);
};

const HIDDEN = '•••••';

/** 12.345 € — ganze Euro, de-DE. Die eine Geldformatierung der Kacheln und Tabellen. */
export const fmtCents = (cents: number, hideAmounts = false): string =>
    hideAmounts ? HIDDEN : `${Math.round(cents / 100).toLocaleString('de-DE')} €`;

/** 12.345,67 € — zwei Nachkommastellen, fuer Drilldown-Tabellen. */
export const fmtCentsExact = (cents: number, hideAmounts = false): string =>
    hideAmounts ? HIDDEN : `${formatCentsPlain(cents)} €`;

/** 12,3 k€ — nur fuer Achsen und enge Balkenbeschriftungen. */
export const fmtCentsShort = (cents: number, hideAmounts = false): string => {
    if (hideAmounts) return '•••';
    const v = cents / 100;
    const a = Math.abs(v);
    if (a >= 1e6) return `${(v / 1e6).toFixed(1)} M€`;
    if (a >= 1e3) return `${(v / 1e3).toFixed(a >= 1e4 ? 0 : 1)} k€`;
    return `${Math.round(v)} €`;
};

/** Ganze Euro — fuer PDF-Spalten. */
export const euroPlain = (cents: number): string =>
    `${Math.round(cents / 100).toLocaleString('de-DE')} €`;

/** Auslastungsfarben der bestehenden Ansicht, unveraendert uebernommen. */
export const workloadFill = (pct: number): string =>
    pct > 100 ? 'bg-red-500 text-white'
        : pct > 85 ? 'bg-orange-600 text-white'
            : pct > 60 ? 'bg-amber-200 text-amber-900'
                : pct > 30 ? 'bg-emerald-600 text-white'
                    : pct > 0 ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-slate-50 text-slate-300';

export const workloadBorder = (pct: number): string =>
    pct > 100 ? 'border-red-600'
        : pct > 85 ? 'border-orange-500'
            : pct > 60 ? 'border-amber-300'
                : pct > 30 ? 'border-emerald-400'
                    : pct > 0 ? 'border-emerald-200'
                        : 'border-slate-100';

/** Anteil in Prozent, nie NaN, nie Infinity. */
export const pctOf = (part: number, whole: number): number =>
    whole > 0 ? Math.round((part / whole) * 100) : 0;

/**
 * Deutscher Klartext der Warnungsarten fuer die Gruppierung im Dialog.
 *
 * Bewusst ueber `string` geschluesselt und nicht ueber `CockpitWarningKind`:
 * die Engine darf eine Art ergaenzen, ohne dass die Oberflaeche bricht. Was
 * hier fehlt, zeigt der Dialog als Rohwert (WarningsModal, `?? kind`).
 */
export const WARNING_LABEL: Record<string, string> = {
    unknown_service: 'Unbekannte Leistungsposition',
    unknown_performer: 'Ausführende Person unbekannt',
    unknown_responsible: 'Verantwortliche Person unbekannt',
    missing_start_date: 'Kein Startdatum',
    missing_end_date: 'Kein Enddatum',
    invoice_without_project: 'Beleg ohne Projekt',
    invoice_without_date: 'Belege ohne gültiges Rechnungsdatum',
    forecast_capped: 'Forecast durch Restmenge gedeckelt',
    string_quantity: 'Menge als Text gespeichert',
    legacy_backfill: 'Altbestand-Sammelnachträge (nicht als Monatsleistung gezählt)',
    negative_quantity: 'Einträge mit negativer Menge (nicht gezählt)',
    zero_price: 'Beauftragte Menge ohne Preis',
    missing_interval: 'Begehung ohne Intervall, Wochenturnus angenommen',
};

/**
 * Belegart in Klartext. Eine Quelle: `DOCUMENT_TYPE_LABEL` aus types.ts, das
 * auch die Belegtitel im PDF stellt. Unbekanntes bleibt als Rohwert sichtbar,
 * statt still zu verschwinden.
 */
const DOCUMENT_TYPE_TEXT: Record<string, string> = { ...DOCUMENT_TYPE_LABEL };

export const documentTypeLabel = (type: string | null | undefined): string => {
    const key = String(type ?? '').trim();
    if (!key) return '—';
    return DOCUMENT_TYPE_TEXT[key] ?? key;
};
